import type { Dispatch, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'

import { newOperationId } from '../../../shared/model/operationIds'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import type { InventoryPorts, PortOutcome } from '../model/ports'
import type { InventoryOperation } from '../model/types'
import {
  inventoryApi,
  onecInventoryClearSession,
  onecInventoryCreateProduct,
  onecInventorySaveSession,
  onecInventoryScan,
  onecInventorySession,
  sessionTag,
} from './inventoryApi'
import { reloadStructure } from '../../structure/api/structureApi'

/**
 * Порты инвентаризации по умолчанию: endpoints общего `baseApi` через
 * ЕДИНСТВЕННЫЙ transport. Listener получает dispatch стора приложения (с
 * thunk middleware), поэтому ответы попадают в общий кэш.
 */
type ThunkCapable = ThunkDispatch<never, unknown, UnknownAction>

/** Пауза автосохранения: сканер печатает быстро, сохранять каждый символ незачем. */
export const AUTOSAVE_DELAY_MS = 800

async function settle<T, R>(
  started: { unwrap: () => Promise<T>; reset?: () => void },
  map: (value: T) => R,
): Promise<PortOutcome<R>> {
  try {
    return { ok: true, value: map(await started.unwrap()) }
  } catch (error) {
    return { ok: false, error }
  } finally {
    // Итог команды — у операции/дельты; запись мутации в кэше не нужна.
    started.reset?.()
  }
}

const identityOf = (operation: InventoryOperation) => ({
  operationId: operation.operationId,
  idempotencyKey: operation.idempotencyKey,
  expectedConfigurationVersion: operation.expectedConfigurationVersion,
})

export function createInventoryPorts(dispatch: Dispatch<UnknownAction>): InventoryPorts {
  const run = dispatch as ThunkCapable
  const sessionArgs = (resourceId: string, warehouseRefKey: string) => ({
    resourceId: normalizeRid(resourceId),
    warehouseRefKey: warehouseRefKey.trim().toLowerCase(),
  })
  return {
    newId: newOperationId,
    now: () => new Date().toISOString(),
    debounceMs: AUTOSAVE_DELAY_MS,
    scan: (resourceId, warehouseRefKey, barcode) =>
      settle(
        // Скан — живое чтение: мимо кэша, иначе только что заведённый товар «не находился» бы.
        run(onecInventoryScan.initiate({ ...sessionArgs(resourceId, warehouseRefKey), barcode }, { forceRefetch: true, subscribe: false })),
        (answer) => answer.data,
      ),
    readSession: (resourceId, warehouseRefKey) =>
      // Свежая сессия с сервера (в 1С не ходит): набор строится поверх того, что сохранило и другое рабочее место.
      settle(
        run(onecInventorySession.initiate(sessionArgs(resourceId, warehouseRefKey), { forceRefetch: true, subscribe: false })),
        (value) => value,
      ),
    saveSession: (resourceId, identity, data) =>
      settle(run(onecInventorySaveSession.initiate({ resourceId: normalizeRid(resourceId), identity, data })), (value) => value),
    storeSession: (resourceId, warehouseRefKey, session) => {
      void run(inventoryApi.util.upsertQueryData('onecInventorySession', sessionArgs(resourceId, warehouseRefKey), session))
    },
    createProduct: (operation) =>
      settle(
        run(
          onecInventoryCreateProduct.initiate({
            resourceId: operation.resourceId,
            identity: identityOf(operation),
            data: { name: operation.payload.name, barcode: operation.payload.barcode, prices: operation.payload.prices },
          }),
        ),
        (answer) => answer.data,
      ),
    clearSession: (operation) =>
      settle(
        run(
          onecInventoryClearSession.initiate({
            resourceId: operation.resourceId,
            warehouseRefKey: operation.warehouseRefKey,
            identity: identityOf(operation),
            data: null,
          }),
        ),
        () => null,
      ),
    reloadSession: (resourceId, warehouseRefKey) => {
      dispatch(inventoryApi.util.invalidateTags([sessionTag(resourceId, warehouseRefKey)]))
    },
    reloadWarehouses: (resourceId) => {
      // Склады — из структуры базы, которую служба читает из 1С на лету (RP-LSB1): «Повторить» после отказа 1С —
      // то же чтение ещё раз (память службы после отказа пуста), общее с поставками и ценниками.
      dispatch(reloadStructure(resourceId))
    },
  }
}
